import AnalyticsSession from "@/helpers/AnalyticsSession.js";
import NetworkV2 from "@vue-components/helpers/NetworkV2.js";

// Actions (clicks, navigations) are sent in batches to keep the database load low
const ACTIONS_URL = `/api/public/analytics/actions/add`;
const ACTIONS_FLUSH_DELAY = 3000;
const MAXIMUM_PENDING_ACTIONS = 20;

// A session lasts this long at most (the server applies the same limit), then the next analytics call starts a new one
const SESSION_MAXIMUM_AGE = 4 * 60 * 60 * 1000;

// The browser storage can be blocked (ex: an embedded cross-domain iframe, privacy modes): even reading
// window.localStorage or window.sessionStorage then throws, so the analytics must work without it.
// The storage is given by name, to be read inside the try.
function readStorage(storageName, key) {
    try {
        return window[storageName].getItem(key);
    } catch (e) {
        return null;
    }
}

function writeStorage(storageName, key, value) {
    try {
        window[storageName].setItem(key, value);
    } catch (e) {
        // The session id is only kept to resume the session after a page load
    }
}

function removeStorage(storageName, key) {
    try {
        window[storageName].removeItem(key);
    } catch (e) {
        // Nothing stored, nothing to remove
    }
}

// Same values, whatever the order of the keys (actions are plain objects, arrays and primitives)
function areValuesEqual(first, second) {
    if (first === second) {
        return true;
    }
    if (typeof first !== `object` || typeof second !== `object` || first === null || second === null || Array.isArray(first) !== Array.isArray(second)) {
        return false;
    }

    const firstKeys = Object.keys(first);
    return firstKeys.length === Object.keys(second).length && firstKeys.every(key => key in second && areValuesEqual(first[key], second[key]));
}

export default class SessionTracker {
    static session;
    static sessionStartedAt = null;
    static hasRegisteredSession = false;
    static pendingActions = [];
    static actionsFlushTimer = null;

    static init(globalSessionInfo = {}) {
        if (this.initialized) {
            return;
        }
        this.initialized = true;

        // Kept for getSessionId() to retry the session registration later (ex: offline at boot)
        this.globalSessionInfo = globalSessionInfo;

        window.SessionTracker = SessionTracker;
        this.trackRegistration(this.retrieveSession(globalSessionInfo));

        // Kept so clean() removes the exact same listeners (a new bind() creates a different function)
        this.boundHandlePageHidden = this.handlePageHidden.bind(this);

        document.addEventListener(`visibilitychange`, this.boundHandlePageHidden);
        window.addEventListener(`pagehide`, this.boundHandlePageHidden);
    }

    static clean() {
        document.removeEventListener(`visibilitychange`, this.boundHandlePageHidden);
        window.removeEventListener(`pagehide`, this.boundHandlePageHidden);
    }

    static handlePageHidden(event) {
        if (event.type === `pagehide` || document.visibilityState === `hidden`) {
            this.flushActionsBeforeLeaving();
        }
    }

    static async createNewSession(globalSessionInfo = {}) {
        const session = new AnalyticsSession({
            userAgent: navigator.userAgent,
            origin: window.location.origin,
            ...globalSessionInfo,
            language: readStorage(`localStorage`, `language`) || navigator.language.substring(0, 2)
        });
        this.session = session;

        if (!window.navigator.onLine)
            return;

        // The id (when resuming a stored session) is part of the body so the server updates that session
        const response = await NetworkV2.post(`/api/public/analytics/sessions/init`, session.toBody());

        // endSession() or startSession() replaced this session while the request was in flight: it is abandoned
        if (this.session !== session) {
            return;
        }

        session.id = response.data.id;
        // The server knows when a resumed session started
        this.sessionStartedAt = Date.parse(response.data.dateCreation) || Date.now();
        this.hasRegisteredSession = true;
        writeStorage(`sessionStorage`, `session_id`, session.id);
    }

    /**
     * Keep the registration in flight in sessionInitPromise so concurrent callers (ex: getSessionId() while
     * the boot registration is pending) wait for it instead of registering a duplicated session.
     * Never rejects: a failed registration is retried by the next getSessionId().
     * @param {Promise<void>} registration
     * @returns {Promise<void>}
     */
    static trackRegistration(registration) {
        const trackedRegistration = registration
            .catch(e => console.error(`Could not register analytics session`, e))
            .finally(() => {
                if (this.sessionInitPromise === trackedRegistration) {
                    this.sessionInitPromise = null;
                }
            });
        this.sessionInitPromise = trackedRegistration;
        return trackedRegistration;
    }

    static async retrieveSession(globalSessionInfo = {}) {
        const sessionId = readStorage(`sessionStorage`, `session_id`);
        if (sessionId && sessionId !== `null`) {
            await this.createNewSession({id: sessionId, ...globalSessionInfo});
        } else {
            await this.createNewSession(globalSessionInfo);
        }
    }

    /**
     * End the current session without starting a new one (ex: kiosk going idle).
     * The next analytics call starts a fresh session through getSessionId() or startSession().
     */
    static endSession() {
        this.flushActionsBeforeLeaving();
        this.session = null;
        this.sessionStartedAt = null;
        this.sessionInitPromise = null;
        removeStorage(`sessionStorage`, `session_id`);
    }

    static isSessionExpired() {
        return this.sessionStartedAt !== null && Date.now() - this.sessionStartedAt > SESSION_MAXIMUM_AGE;
    }

    /**
     * Session info without the landing page: a session that starts later than the visit (ex: the previous one expired)
     * has no landing page, the pages are recorded as they are visited.
     */
    static getSessionInfoWithoutPage() {
        const sessionInfo = {...this.globalSessionInfo};
        delete sessionInfo.page;
        return sessionInfo;
    }

    /**
     * Start a new session, typically when leaving the kiosk idle screen. The boot-time session info
     * (dealer, website, medium, source...) is kept and overridden by sessionInfo, ex: the landing page.
     * Returns null while offline or on failure.
     * @param {object} sessionInfo
     * @returns {Promise<string|null>}
     */
    static async startSession(sessionInfo = {}) {
        this.endSession();
        if (!window.navigator.onLine) {
            return null;
        }
        await this.trackRegistration(this.createNewSession({...this.globalSessionInfo, ...sessionInfo}));
        return this.session?.id || null;
    }

    /**
     * Resolve the analytics session id, registering the session with the server first when it
     * couldn't be done yet (ex: offline at boot). Returns null while offline or on failure, so
     * callers can skip their analytics call instead of sending an orphan sessionId.
     * @returns {Promise<string|null>}
     */
    static async getSessionId() {
        // An expired session ends (its queued actions are sent with it), the next one starts below
        if (this.session?.id && this.isSessionExpired()) {
            this.endSession();
        }
        if (this.session?.id) {
            return this.session.id;
        }
        if (!this.sessionInitPromise) {
            if (!window.navigator.onLine) {
                return null;
            }
            // Until a session was registered, the boot registration is retried with its landing page
            this.trackRegistration(this.retrieveSession(this.hasRegisteredSession ? this.getSessionInfoWithoutPage() : this.globalSessionInfo));
        }
        await this.sessionInitPromise;
        return this.session?.id || null;
    }

    /**
     * Record a click or navigation. Cheap: the action is only queued, and sent in a batch shortly after
     * with its session. Ignored when the analytics are not running (no dealer or page to attach to).
     * @param {{type: string, page: {id: string, permalink: string}, block?: object, cmsItem?: object, target: object}} action See CmsAnalyticsActionModel.
     * The same action as the previous one, not sent yet, only increments its count.
     */
    static trackAction(action) {
        if (!this.initialized) {
            return;
        }

        // The same action again right after (ex: 20 clicks on the same link) is counted, not stored 20 times
        const previousAction = this.pendingActions[this.pendingActions.length - 1];
        if (previousAction) {
            const previousFields = {...previousAction};
            delete previousFields.date;
            delete previousFields.count;
            if (areValuesEqual(previousFields, action)) {
                previousAction.count += 1;
                return;
            }
        }

        this.pendingActions.push({...action, date: new Date(), count: 1});

        // console.log(action);
        if (this.pendingActions.length >= MAXIMUM_PENDING_ACTIONS) {
            this.flushActions();
        } else if (!this.actionsFlushTimer) {
            this.actionsFlushTimer = setTimeout(() => this.flushActions(), ACTIONS_FLUSH_DELAY);
        }
    }

    static getActionsBody(sessionId, actions) {
        return {
            sessionId,
            dealerid: this.globalSessionInfo.dealerid,
            websiteId: this.globalSessionInfo.websiteId,
            excluded: this.globalSessionInfo.excluded,
            actions
        };
    }

    static takePendingActions() {
        clearTimeout(this.actionsFlushTimer);
        this.actionsFlushTimer = null;
        return this.pendingActions.splice(0);
    }

    /**
     * Send the queued actions. Dropped while offline or when the session can't be registered, like the other analytics.
     * @returns {Promise<void>}
     */
    static async flushActions() {
        const actions = this.takePendingActions();
        if (actions.length === 0) {
            return;
        }

        const sessionId = await this.getSessionId();
        if (!sessionId) {
            return;
        }

        try {
            await NetworkV2.post(ACTIONS_URL, this.getActionsBody(sessionId, actions));
            // console.log(`Sending Actions`);
        } catch (e) {
            console.error(`Could not send analytics actions`, e);
        }
    }

    /**
     * Send the queued actions with the session that is current right now, in a request that survives the page
     * being closed (a link opening the page elsewhere), or the session ending.
     */
    static flushActionsBeforeLeaving() {
        if (this.pendingActions.length === 0) {
            return;
        }

        const actions = this.takePendingActions();
        if (!this.session?.id || !window.navigator.onLine) {
            return;
        }

        const body = JSON.stringify(this.getActionsBody(this.session.id, actions));
        const requestUrl = NetworkV2.getBaseUrl() + ACTIONS_URL;
        if (!navigator.sendBeacon?.(requestUrl, new Blob([body], {type: `application/json`}))) {
            fetch(requestUrl, {method: `POST`, headers: {"content-type": `application/json`}, body, keepalive: true}).catch(() => {});
        }
    }
}
